import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Canonical — mount once at App-level.
 * On every route change, injects/updates a <link rel="canonical"> in <head>
 * pointing to the production origin (getvirtualgrowth.com) + current pathname.
 *
 * Also injects an og:url meta tag so social crawlers see the canonical URL.
 *
 * Trailing slashes are stripped except for the root "/" so that pathname
 * variants collapse to a single canonical.
 */
const PROD_ORIGIN = "https://getvirtualgrowth.com";

function normalisePath(pathname) {
  if (!pathname) return "/";
  if (pathname === "/") return "/";
  // Strip a trailing slash to collapse "/foo/" and "/foo" to the same canonical.
  return pathname.replace(/\/+$/, "");
}

function upsert(selector, tag, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

export default function Canonical() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const path = normalisePath(pathname);
    // For canonicalisation we deliberately drop query params so that UTM/session
    // parameters do not create infinite duplicate variants. This is the standard
    // SEO practice for content routes.
    const url = PROD_ORIGIN + path;

    upsert('link[rel="canonical"]', "link", { rel: "canonical", href: url });
    upsert('meta[property="og:url"]', "meta", { property: "og:url", content: url });
    upsert('meta[name="twitter:url"]', "meta", { name: "twitter:url", content: url });

    // Keep silent — no effect on rendering. Search-only.
    // (search intentionally unused for canonical; kept as a dep so the effect
    // re-runs on client navigations that only change query strings.)
  }, [pathname, search]);

  return null;
}
